import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { archie } from "@smart/bridge-images-dom";
import { aiFillSectionUri } from "@smart/bridge-types-basic";
import {
  Button,
  DroppableItem,
  Icon,
  Switch,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";
import { sortField } from "@smart/itops-utils-basic";
import { useAnalyticsEvent } from "@smart/manage-hooks-dom";

import { EditSectionsModal } from "./edit-sections-modal";
import { FeatureInfo } from "./feature-info";
import { NavItem, selectedClassNames } from "./nav-item";
import { LanguageKey } from "../../../types";
import {
  EditableSection,
  GqlForm,
  UpdateAIFillSettings,
  UpdatedSection,
} from "../types";
import { getTranslatedSections, SectionChangeCounts } from "../utils";

type SectionNavigationProps = {
  form?: GqlForm | null;
  sections: EditableSection[];
  currentSectionUri: string;
  disabled?: boolean;
  loading: {
    form: boolean;
    sections: boolean;
  };
  setCurrentSectionUri: (sectionUri: string) => void;
  updateAllSections: (sections: UpdatedSection[]) => Promise<void>;
  aiFillSettings: GqlForm["aiFillSettings"];
  updateAIFillSettings: UpdateAIFillSettings;
  viewingLanguage: LanguageKey;
  sectionChangeCounts?: SectionChangeCounts;
  removedPublishedSections?: EditableSection[];
};

const SectionNavigation = ({
  form,
  sections,
  currentSectionUri,
  disabled,
  loading,
  setCurrentSectionUri,
  updateAllSections,
  aiFillSettings,
  updateAIFillSettings,
  viewingLanguage,
  sectionChangeCounts,
  removedPublishedSections,
}: SectionNavigationProps) => {
  const { publishEvent } = useAnalyticsEvent();
  const [sectionsModal, showSectionsModal] = useState(false);
  const allowAiFillRef = useRef<boolean | undefined>(
    aiFillSettings?.allowAiFill,
  );

  const translatedSections = getTranslatedSections(sections, viewingLanguage);
  const translatedRemovedSections = getTranslatedSections(
    removedPublishedSections ?? [],
    viewingLanguage,
  );

  const allNavSections = sortField(
    [
      ...translatedSections.map((s) => ({ ...s, isRemoved: false as const })),
      ...translatedRemovedSections.map((s) => ({
        ...s,
        isRemoved: true as const,
      })),
    ],
    { key: (s) => s.values?.order, dir: "asc" },
  );

  const activeSectionIndexByUri = new Map(
    translatedSections.map((s, i) => [s.uri, i]),
  );

  useEffect(() => {
    const currentAllowAiFill = !!aiFillSettings?.allowAiFill;
    if (!currentAllowAiFill && currentSectionUri === aiFillSectionUri) {
      setCurrentSectionUri(translatedSections[0].uri);
    }
    if (
      currentAllowAiFill &&
      !allowAiFillRef.current &&
      currentSectionUri !== aiFillSectionUri
    ) {
      setCurrentSectionUri(aiFillSectionUri);
    }
    (allowAiFillRef as React.MutableRefObject<boolean | undefined>).current =
      currentAllowAiFill;
  }, [!!aiFillSettings?.allowAiFill]);

  return (
    <div
      data-testid="sections-navigation"
      className="w-[32.4rem] flex flex-col"
    >
      <div className="flex justify-between items-center gap-medium px-[2.4rem] mt-[1.6rem]">
        <h2 className="text-paragraph font-semibold text-neutral-850">
          Sections
        </h2>
        <Button
          variant="inlineDefault"
          text="Edit"
          disabled={loading.sections || disabled}
          onClick={() => showSectionsModal(true)}
        />
      </div>
      <div className="border-t border-neutral-100 border-solid my-[1.6rem]" />
      <div className="px-[0.8rem]">
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          data-testid="ai-fill-section-nav"
          aria-disabled={!aiFillSettings?.allowAiFill}
          className={clsx(
            "flex items-center w-full h-[4rem] py-[0.4rem] mb-[1.6rem] rounded border border-transparent",
            aiFillSettings?.allowAiFill && "cursor-pointer hover:bg-cyan-140",
            currentSectionUri === aiFillSectionUri && selectedClassNames,
          )}
          onClick={() => {
            if (aiFillSettings?.allowAiFill)
              setCurrentSectionUri(aiFillSectionUri);
          }}
        >
          <FeatureInfo
            info={
              <div>
                <p className="mb-[1rem]">
                  <b>Meet the Autofill Assistant!</b>
                </p>
                <p className="mx-0 my-[0.5rem]">
                  It lets your clients automatically populate their intake form
                  by uploading relevant documents, which means faster form
                  completion!
                  <br />
                  <br />
                  Simply tell them which documents to upload.
                </p>
              </div>
            }
            featureName="new-autofill-assistant"
            placement="right"
            hideInfoPopup={currentSectionUri === aiFillSectionUri}
          >
            <div className="flex gap-medium px-[1.6rem]">
              <img
                className={twMerge(
                  clsx(
                    "w-[3.2rem] opacity-50",
                    aiFillSettings?.allowAiFill && "opacity-100",
                  ),
                )}
                src={archie}
                alt="Archie"
              />
              <div className="flex justify-between items-center flex-1">
                <div className="flex items-center gap-small">
                  <span
                    className={clsx(
                      "text-ui font-semibold whitespace-nowrap",
                      aiFillSettings?.allowAiFill
                        ? "text-neutral-600"
                        : "text-neutral-400",
                    )}
                  >
                    Autofill Assistant
                  </span>
                  <Tooltip
                    className="flex items-center"
                    text="Allows clients to automatically populate intake forms by uploading relevant documents."
                    placement="bottom"
                    offsetBy={7}
                    mountTo="body"
                  >
                    <Icon className="text-blue-560" name="regularCircleInfo" />
                  </Tooltip>
                </div>
                <Switch
                  toggled={!!form?.aiFillSettings?.allowAiFill}
                  onClick={() => {
                    updateAIFillSettings({
                      allowAiFill: !form?.aiFillSettings?.allowAiFill,
                    }).catch(console.error);

                    publishEvent({
                      category: "track",
                      name: "FormBuilderAutofillAssistantToggle",
                      screen: "FormBuilder",
                      entryPoint: "FormBuilderSections",
                      component: "ToggleClicked",
                      jiraKey: "LINK-3002",
                      interactionType: "Active",
                      properties: {
                        Status: !form?.aiFillSettings?.allowAiFill
                          ? "On"
                          : "Off",
                      },
                    }).catch(console.error);
                  }}
                  dataTestId="ai-fill-toggle"
                  disabled={disabled || loading.form}
                />
              </div>
            </div>
          </FeatureInfo>
        </div>
        <div className="flex flex-col gap-[0.4rem]">
          {allNavSections.map((section) =>
            section.isRemoved ? (
              <NavItem
                key={section.uri}
                section={section}
                index={NaN}
                loading={false}
                currentSectionUri={currentSectionUri}
                setCurrentSectionUri={() => {}}
                isOver={false}
                isRemoved
              />
            ) : (
              <DroppableItem
                key={section.uri}
                id={section.uri}
                disabled={section.uri === currentSectionUri}
              >
                {({ isOver }) => (
                  <NavItem
                    key={section.uri}
                    section={section}
                    index={activeSectionIndexByUri.get(section.uri) ?? 0}
                    loading={loading.sections}
                    currentSectionUri={currentSectionUri}
                    setCurrentSectionUri={setCurrentSectionUri}
                    isOver={isOver}
                    hasUnpublishedChanges={
                      (sectionChangeCounts?.get(section.uri) ?? 0) > 0
                    }
                  />
                )}
              </DroppableItem>
            ),
          )}
        </div>
      </div>
      <EditSectionsModal
        editSectionsModal={sectionsModal}
        onClose={() => showSectionsModal(false)}
        sections={sections}
        updateAllSections={updateAllSections}
        aiFillSettings={aiFillSettings}
        updateAIFillSettings={updateAIFillSettings}
        viewingLanguage={viewingLanguage}
      />
    </div>
  );
};

export { SectionNavigation, SectionNavigationProps };
